import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Percent, Clock, KeyRound } from "lucide-react";
import { scrollTo } from "./Header";

const IMG = "https://images.unsplash.com/photo-1766832255363-c9f060ade8b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxyZXN0YXVyYW50JTIwY2hlZiUyMGRhcmslMjBwcmVtaXVtfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const stats = [
  { icon: Percent, value: "0%", label: "Commission. Forever." },
  { icon: Clock, value: "< 7 days", label: "From order to launch" },
  { icon: KeyRound, value: "100%", label: "Yours. Data included." },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-ice/40 blur-3xl -z-10" />
      <div className="absolute top-96 -left-40 w-[420px] h-[420px] rounded-full bg-brand/10 blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="inline-flex items-center gap-2.5 rounded-full border border-black/10 bg-white px-5 py-2 shadow-sm"
        >
          <span className="w-2 h-2 rounded-full bg-brand" />
          <span data-testid="hero-eyebrow" className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/60">
            One-time price · Zero commission · Live in under 7 days
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-8 font-display font-semibold text-lg text-brand"
        >
          Your shop. Your apps. Your customers.
        </motion.p>

        <h1 className="mt-4 font-display font-black uppercase tracking-tighter leading-[0.95] text-[12vw] md:text-[8vw] lg:text-[6vw] text-ink">
          <RevealLine delay={0.65}><span className="whitespace-nowrap">Stop renting</span></RevealLine>
          <RevealLine delay={0.8}>
            <span className="text-outline">your customers.</span>
          </RevealLine>
          <RevealLine delay={0.95}>
            <span className="text-brand">Own them.</span>
          </RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-8 max-w-2xl mx-auto text-ink/65 text-base md:text-lg font-light leading-relaxed"
        >
          simplyOwner is the complete ordering system for takeaway &amp; delivery businesses — your homepage, shop system,
          payments, white-label mobile apps and customer data. Paid once. Yours forever.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollTo("#contact")}
            className="group flex items-center gap-3 bg-brand hover:bg-black text-white font-display font-bold uppercase tracking-wide px-9 py-4 rounded-full transition-colors duration-300 card-glow-strong"
          >
            Become the owner
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollTo("/pricing")}
            className="font-display font-bold uppercase tracking-wide px-9 py-4 rounded-full border border-ink/20 text-ink hover:border-brand hover:text-brand transition-colors duration-300"
          >
            See pricing &amp; savings
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-4"
        >
          {stats.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.value.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="flex items-center gap-2.5">
              <s.icon className="w-4 h-4 text-brand shrink-0" />
              <span className="font-display font-bold text-sm text-ink">{s.value}</span>
              <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-black/45">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-12 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="rounded-2xl overflow-hidden border border-black/10 card-glow-strong">
            <motion.img style={{ y: imgY }} src={IMG} alt="Restaurant owner in his kitchen" className="w-full aspect-[21/9] object-cover spotlight-img scale-110" />
          </div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 2 }}
            className="absolute left-8 bottom-8 bg-white rounded-xl border border-black/10 shadow-xl px-6 py-4"
          >
            <div className="font-display font-black text-3xl text-brand leading-none">0%</div>
            <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-black/50 mt-1">commission on every order</div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
